import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Progress, Pagination, Carousel, Modal } from "antd";
import { useTranslation } from "react-i18next";

import { promiseDispatch, getFileName } from "@/util";
import { selectUser, selectOperationVersion } from "@/selector";
import { actionCreators } from "@/duck/dashboard";
import "@/components/dashboard/misc/Order.less";

const MiniContent = ({ records, openModal }) => {
  const { t } = useTranslation();
  const items = records.map((r) => {
    const { thread, step } = r;
    const p = ((step / 7) * 100).toFixed(0);
    return (
      <tr key={`order-item-${thread}`}>
        {step === 7 ? (
          <td>
            <span
              className="ant-btn-link"
              style={{ cursor: "pointer" }}
              onClick={() => openModal(r.uuid)}
            >
              {r.createTime}
            </span>
          </td>
        ) : (
          <td>{r.createTime}</td>
        )}
        <td>
          <Progress percent={p} size="small" />
        </td>
      </tr>
    );
  });
  return (
    <table className="pure-table pure-table-striped">
      <thead>
        <tr>
          <th className="date">{t("order.date")}</th>
          <th className="status">{t("order.status")}</th>
        </tr>
      </thead>
      <tbody>{items}</tbody>
    </table>
  );
};

const Content = ({ records }) => {
  const { t } = useTranslation();

  const items = records.map((r) => {
    const { thread, step } = r;
    const p = ((step / 7) * 100).toFixed(0);
    return (
      <tr key={`order-item-${thread}`}>
        <td>{r.createTime}</td>
        <td>{r.userName}</td>
        <td>{r.mail}</td>
        <td>{`(${r.longitude}, ${r.latitude})`}</td>
        <td>{t("order.result")}</td>
        <td>
          <Progress percent={p} size="small" />
        </td>
      </tr>
    );
  });
  return (
    <table className="pure-table pure-table-striped">
      <thead>
        <tr>
          <th className="date">{t("order.date")}</th>
          <th className="username">{t("order.username")}</th>
          <th className="email">{t("order.email")}</th>
          <th className="point">{t("order.point")}</th>
          <th className="ftp">{t("order.ftp")}</th>
          <th className="status">{t("order.status")}</th>
        </tr>
      </thead>
      <tbody>{items}</tbody>
    </table>
  );
};

const noImageStyle = {
  height: "720px",
  color: "rgba(0,0,0, 0.75)",
  lineHeight: "720px",
  textAlign: "center",
  background: "rgba(0,0,0, 0.15)",
};

const imageStyle = {
  width: "952px",
  height: "720px",
  background: "rgba(0,0,0, 0.15)",
};
const labelStyle = {
  color: "rgba(0, 0, 0, 0.55)",
};

const Order = ({ maxium }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [content, setContent] = useState([]);
  const [size, setSize] = useState(0);
  const [visible, setVisible] = useState(false);
  const [images, setImages] = useState([]);
  const user = useSelector(selectUser);
  const operationVersion = useSelector(selectOperationVersion);
  const dispatch = useDispatch();

  useEffect(() => {
    promiseDispatch(dispatch, actionCreators.dashboard.panel.query.order, {
      userId: user.id,
      page,
      operationVersion,
    }).then((response) => {
      // setPage(response["pageable"]["pageNumber"] + 1);
      setTotal(response["totalElements"]);
      setContent(response["content"]);
    });
    const interval = setInterval(() => {
      promiseDispatch(dispatch, actionCreators.dashboard.panel.query.order, {
        userId: user.id,
        page,
        operationVersion,
      }).then((response) => {
        setTotal(response["totalElements"]);
        setContent(response["content"]);
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [dispatch, user, page, operationVersion]);

  const onPageChange = useCallback(
    (page) => {
      setPage(page);
    },
    [setPage]
  );
  const closeModal = useCallback(() => {
    setVisible(false);
    setImages([]);
    setSize(0);
  }, [setVisible]);
  const openModal = useCallback(
    (uuid) => {
      setVisible(true);
      promiseDispatch(dispatch, actionCreators.dashboard.panel.query.image, {
        userId: user.id,
        uuid,
      }).then((response) => {
        setImages(response);
      });
      promiseDispatch(dispatch, actionCreators.dashboard.panel.query.size, {
        uuid,
      }).then((response) => {
        setSize(response.result);
      });
    },
    [dispatch, user]
  );

  if (content.length === 0) {
    return <div style={{ textAlign: "center" }}>{t("order.empty")}</div>;
  }

  let imageElements;
  if (images.length === 0) {
    imageElements = (
      <div>
        <h5 style={noImageStyle}>{t("order.empty")}</h5>
      </div>
    );
  } else {
    imageElements = images.map((i) => {
      const fileName = getFileName(i);
      return (
        <div key={`img-${i}`} style={{ position: "relative" }}>
          <h3 style={labelStyle}>
            {fileName} {size ? `(${size}*${size})` : ""}
          </h3>
          <img src={`/compute${i}`} style={imageStyle} alt="" />
        </div>
      );
    });
  }

  return (
    <div className="order-container">
      {maxium ? (
        <Content records={content} />
      ) : (
        <MiniContent records={content} openModal={openModal} />
      )}
      {content.length !== 0 && (
        <Pagination
          showSizeChanger={false}
          current={page}
          defaultCurrent={1}
          size="small"
          total={total}
          onChange={(page) => onPageChange(page)}
          style={{ marginTop: 5, paddingBottom: 10 }}
        />
      )}
      <Modal
        destroyOnClose={true}
        visible={visible}
        onCancel={closeModal}
        footer={null}
        centered={true}
        width={1000}
      >
        <Carousel>{imageElements}</Carousel>
      </Modal>
    </div>
  );
};

export default Order;
